import React from "react";
import { Chip, SvgIcon } from "@mui/material";
import type { ChipProps, SxProps, Theme } from "@mui/material";
import { OrderStatus } from "client/api-v2";
import { orderStatusLabel } from "areas/orders/utils/orderStatusLabel";

const ring = <circle cx="12" cy="12" r="9" />;

const statusGlyphs: Record<OrderStatus, { name: string; shape: React.ReactNode }> = {
  [OrderStatus.New]: { name: "ring", shape: ring },
  [OrderStatus.Quote]: {
    name: "ring-dot",
    shape: (
      <>
        {ring}
        <circle cx="12" cy="12" r="3" fill="currentColor" stroke="none" />
      </>
    ),
  },
  [OrderStatus.Accepted]: {
    name: "ring-check",
    shape: (
      <>
        {ring}
        <path d="M8 12.5l2.5 2.5L16 9" />
      </>
    ),
  },
  [OrderStatus.Cancelled]: {
    name: "ring-cross",
    shape: (
      <>
        {ring}
        <path d="M9 9l6 6M15 9l-6 6" />
      </>
    ),
  },
  [OrderStatus.Expired]: {
    name: "ring-clock",
    shape: (
      <>
        {ring}
        <path d="M12 7v5l3.5 2" />
      </>
    ),
  },
};

const statusColors: Record<OrderStatus, ChipProps["color"]> = {
  [OrderStatus.New]: "default",
  [OrderStatus.Quote]: "info",
  [OrderStatus.Accepted]: "success",
  [OrderStatus.Cancelled]: "error",
  [OrderStatus.Expired]: "warning",
};

export function OrderStatusChip({ status, sx }: { status: OrderStatus; sx?: SxProps<Theme> }) {
  const { name, shape } = statusGlyphs[status];

  return (
    <Chip
      label={orderStatusLabel(status)}
      color={statusColors[status]}
      size="small"
      variant="outlined"
      sx={sx}
      icon={
        <SvgIcon
          viewBox="0 0 24 24"
          fontSize="small"
          aria-hidden="true"
          data-testid={`order-status-glyph-${status}`}
          data-glyph={name}
          sx={{ fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" }}
        >
          {shape}
        </SvgIcon>
      }
    />
  );
}
